import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Download, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useOutletContext } from 'react-router-dom';
import { getAnalytics } from '../../utils/api';
import type { Analytics } from '../../utils/types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAdminLang } from '../AdminLangContext';

interface AdminContext {
  secret: string;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatDayName(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return DAY_NAMES[d.getDay()] ?? dateStr;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function pctStr(n: number): string {
  return (n * 100).toFixed(1) + '%';
}

export default function AdminAnalytics() {
  const { secret } = useOutletContext<AdminContext>();
  const { lang } = useAdminLang();
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setIsLoading(true);
    setError(null);
    getAnalytics(secret, lang)
      .then(setAnalytics)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load analytics'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, [secret, lang]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="h-5 w-5 animate-spin" />
          Loading analytics...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" onClick={load} className="mt-3">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const summary = analytics.summary ?? {
    totalSearches: analytics.totalSearches,
    uniqueQueries: 0,
    avgQueryTimeMs: analytics.averageResponseTime,
    zeroResultRate: 0,
    totalClicks: 0,
    overallCtr: 0,
  };

  const dailyVolume = analytics.dailyVolume ?? [];
  const topQueriesWithCtr = analytics.topQueriesWithCtr ?? [];

  // Compute trend from first-half vs second-half of dailyVolume
  const halfIdx = Math.floor(dailyVolume.length / 2);
  const firstHalf = dailyVolume.slice(0, halfIdx);
  const secondHalf = dailyVolume.slice(halfIdx);
  const avgFirst = firstHalf.length > 0 ? firstHalf.reduce((s, d) => s + d.totalSearches, 0) / firstHalf.length : 0;
  const avgSecond = secondHalf.length > 0 ? secondHalf.reduce((s, d) => s + d.totalSearches, 0) / secondHalf.length : 0;
  const volumeTrend = avgFirst > 0 ? ((avgSecond - avgFirst) / avgFirst) * 100 : 0;

  // Chart data: daily searches + clicks (estimated from total CTR)
  const chartData = dailyVolume.map((d) => ({
    day: formatDayName(d.date),
    searches: d.totalSearches,
    clicks: Math.round(d.totalSearches * summary.overallCtr),
  }));

  // Funnel data
  const funnelSteps = [
    { label: 'Searches', count: summary.totalSearches, pct: 1 },
    { label: 'Clicked Result', count: summary.totalClicks, pct: summary.overallCtr },
  ];

  // Export CSV handler
  const handleExportCsv = () => {
    if (!topQueriesWithCtr.length) return;
    const header = 'Query,Volume,CTR,Clicks\n';
    const rows = topQueriesWithCtr
      .map((q) => `"${q.query}",${q.searches},${(q.ctr * 100).toFixed(1)}%,${q.clicks}`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analytics-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analytics Dashboard</h1>
          <p className="text-muted-foreground">Search performance insights for the last 7 days</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Searches"
          value={formatNumber(summary.totalSearches)}
          trend={volumeTrend}
        />
        <KpiCard
          label="Avg CTR"
          value={pctStr(summary.overallCtr)}
          trend={null}
        />
        <KpiCard
          label="Zero-Result Rate"
          value={pctStr(summary.zeroResultRate)}
          trend={null}
          invertTrend
        />
        <KpiCard
          label="Avg Response Time"
          value={`${Math.round(summary.avgQueryTimeMs)}ms`}
          trend={null}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Search Volume Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Search Volume</CardTitle>
            <CardDescription>Daily search volume and clicks</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="day" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid hsl(var(--border))',
                      background: 'white',
                      fontSize: 13,
                    }}
                  />
                  <Legend />
                  <Bar dataKey="searches" fill="#4f6bed" radius={[3, 3, 0, 0]} name="Searches" />
                  <Bar dataKey="clicks" fill="#22c55e" radius={[3, 3, 0, 0]} name="Clicks" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Search-to-Click Funnel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Search-to-Click Funnel</CardTitle>
            <CardDescription>Conversion funnel from search to click</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-5 pt-2">
              {funnelSteps.map((step, i) => {
                const widthPct = Math.max(step.pct * 100, 4);
                const colors = ['#4f6bed', '#22c55e'];
                return (
                  <div key={step.label}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium">{step.label}</span>
                      <span className="text-sm text-muted-foreground font-mono">
                        {formatNumber(step.count)} ({pctStr(step.pct)})
                      </span>
                    </div>
                    <div className="h-8 w-full rounded bg-muted/40">
                      <div
                        className="h-full rounded transition-all"
                        style={{ width: `${widthPct}%`, backgroundColor: colors[i] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Queries Table */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Top Queries</CardTitle>
            <CardDescription>Most popular search queries and their performance</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="h-4 w-4 mr-1.5" /> Export
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {topQueriesWithCtr.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Volume</TableHead>
                  <TableHead className="text-right">CTR</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topQueriesWithCtr.map((q, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-muted-foreground font-mono">{i + 1}</TableCell>
                    <TableCell className="font-medium">{q.query}</TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(q.searches)}</TableCell>
                    <TableCell className="text-right font-mono">{pctStr(q.ctr)}</TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(q.clicks)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-12 text-center text-muted-foreground text-sm">
              No search data yet. Queries will appear here once users start searching.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Zero-Result Queries */}
      {analytics.zeroResultQueries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Zero-Result Queries
            </CardTitle>
            <CardDescription>
              Queries that returned no results &mdash; opportunities to improve
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.zeroResultQueries.slice(0, 8).map((q, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg border bg-amber-50/50 border-amber-200/60"
                >
                  <div>
                    <p className="text-sm font-medium">&ldquo;{q.query}&rdquo;</p>
                    <p className="text-xs text-muted-foreground">{q.count} searches</p>
                  </div>
                  <Badge variant="outline" className="text-xs whitespace-nowrap">
                    Add synonym or review catalog
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Abandoned Searches */}
      {analytics.abandonedSearches && analytics.abandonedSearches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Abandoned Searches</CardTitle>
            <CardDescription>
              Searches that had results but users didn't click anything
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Abandonments</TableHead>
                  <TableHead className="text-right">Avg Results</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.abandonedSearches.slice(0, 10).map((q, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{q.query}</TableCell>
                    <TableCell className="text-right font-mono">{formatNumber(q.count)}</TableCell>
                    <TableCell className="text-right font-mono">{q.avgResultCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI Card sub-component
// ---------------------------------------------------------------------------

function KpiCard({
  label,
  value,
  trend,
  invertTrend = false,
}: {
  label: string;
  value: string;
  trend: number | null;
  invertTrend?: boolean;
}) {
  const showTrend = trend !== null && isFinite(trend) && trend !== 0;
  const isPositive = trend !== null && trend > 0;
  const trendGood = invertTrend ? !isPositive : isPositive;

  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {showTrend ? (
          <div className="flex items-center gap-1 mt-1">
            {trendGood ? (
              <TrendingUp className="h-4 w-4 text-green-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
            <span className={`text-sm font-medium ${trendGood ? 'text-green-600' : 'text-red-500'}`}>
              {isPositive ? '+' : ''}{trend.toFixed(1)}%
            </span>
          </div>
        ) : (
          <div className="h-5 mt-1" />
        )}
      </CardContent>
    </Card>
  );
}
